import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { UserDomain } from '../types/domains.ts';
import { usePartnerDetails } from './usePartnerDetails.ts';

// Hook to get all user domains (only for white-label partners)
export function useUserDomain() {
  const { user } = useAuth();
  const { data: partnerDetails } = usePartnerDetails();
  
  return useQuery({
    queryKey: ['user-domain', user?.id, partnerDetails?.partner_type],
    queryFn: async (): Promise<UserDomain | null> => {
      if (!user) throw new Error('Not authenticated');
      
      // Only white-label partners can access domains
      if (partnerDetails?.partner_type !== 'white_label') {
        return null;
      }
      
      const { data, error } = await supabase
        .from('user_domains')
        .select('domain, status, id, instructions, email_status, postmark_dns_records, postmark_sender_id, email_from_address, reply_to_address')
        .eq('user_id', user.id)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
      return data || null;
    },
    enabled: !!user && !!partnerDetails,
  });
}

// Hook to delete domain (only for white-label partners)
export function useDeleteDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (domainId: string): Promise<void> => {


      const { error } = await supabase
        .from('user_domains')
        .delete()
        .eq('id', domainId)

      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-domain'] }),
    onError: (error) => console.error('Failed to delete domain:', error),
  });
}